import { AnnotationCanvas } from '@/lib/annotations/canvas'
import { AnnotationHistoryManager } from '@/lib/annotations/history-manager'
import { colorToString, DEFAULT_LINE_WIDTH } from '@/lib/annotations/constants'
import useAnnotationPublisher from '@/lib/annotations/publisher'
import useSocketStateSync from '@/lib/annotations/socket-state-sync'
import useAnnotationSubscriber from '@/lib/annotations/subscriber'
import type { AnnotationColors } from '@/lib/annotations/constants'

export interface UseAnnotations {
  undo(): void
  reset(): void
}

export function useAnnotations({
  canvas,
  container,
  active,
  stroke,
  lineWidth,
  aspect,
  zoom,
  pan,
  uvCoords,
  objectFit,
  disabled,
  annotate,
  update,
  subscribe,
}: {
  canvas: Readonly<Ref<HTMLCanvasElement | undefined>>
  container: Readonly<Ref<HTMLElement | undefined>>
  active: Readonly<Ref<boolean>>
  stroke: Readonly<Ref<AnnotationColors>>
  lineWidth: Readonly<Ref<number>>
  aspect: Readonly<Ref<number>>
  zoom: Readonly<Ref<number>>
  pan: Readonly<Ref<Point>>
  uvCoords: Readonly<Ref<Point | null>>
  objectFit: Readonly<Ref<'contain' | 'cover'>>
  disabled: Readonly<Ref<boolean>>
  annotate(drawSpec: { action: string; payload?: Object }): void
  update(payload: { stroke?: number; lineWidth?: number; container?: HTMLElement; reset?(): void; undo?(): void }): void
  subscribe(subscriber: DrawingObserver): void
}) {
  let drawPad: AnnotationCanvas | undefined
  let annotations: AnnotationHistoryManager | undefined

  watch(
    [canvas, container],
    ([canvas, container]) => {
      if (!canvas || !container) {
        return
      }

      drawPad = new AnnotationCanvas(canvas)

      if (annotations) {
        // already have one, re-initialize but carry state forward
        annotations = new AnnotationHistoryManager(drawPad, container).copy(annotations)
      } else {
        annotations = new AnnotationHistoryManager(drawPad, container)
      }

      const initSetup = { strokeStyle: colorToString(stroke.value), lineWidth: DEFAULT_LINE_WIDTH }
      drawPad.setup(initSetup)
      drawPad.setObjectFit(objectFit.value)
      drawPad.setAspect(aspect.value)

      annotations.observe(useAnnotationPublisher({ publish: annotate }))
      annotations?.trigger('setup', initSetup)

      annotations.observe(
        useSocketStateSync({
          setLineWidth: lineWidth => update({ lineWidth }),
          setStroke: stroke => update({ stroke }),
        })
      )

      annotations.setPanCoords(pan.value)
      annotations.setZoomFactor(zoom.value)
      annotations.setUvCoords(uvCoords.value)

      update({ container, reset: () => annotations?.trigger('reset'), undo: () => annotations?.trigger('undo') })
      subscribe(useAnnotationSubscriber(annotations))
    },
    { immediate: true }
  )

  onUnmounted(() => {
    annotations?.deactivate()
  })

  watch(aspect, aspect => drawPad?.setAspect(aspect))
  watch(objectFit, objectFit => drawPad?.setObjectFit(objectFit))
  watch(pan, pan => annotations?.setPanCoords(pan))
  watch(zoom, zoom => annotations?.setZoomFactor(zoom))
  watch(uvCoords, uvCoords => annotations?.setUvCoords(uvCoords))

  watch(stroke, stroke => {
    // 0 is a valid value (#000000 = black)
    if (typeof stroke !== 'number') return
    annotations?.trigger('setup', { strokeStyle: colorToString(stroke) })
  })

  watch(lineWidth, lineWidth => {
    // 0 is an invalid value
    if (!lineWidth) return
    annotations?.trigger('setup', { lineWidth })
  })

  watch([active, disabled], ([active, disabled]) => {
    if (active && !disabled) {
      annotations?.activate()
    } else {
      annotations?.deactivate()
    }
  })

  const undo = () => annotations?.trigger('undo')
  const reset = () => annotations?.trigger('reset')

  return { undo, reset }
}
